import { useState, useEffect } from 'react'
import ToastMsg from '../../Components/ToastMsg/ToastMsg';
import {
    Box, Grid, Stack, Paper, Typography, Button, FormControlLabel,
    TextField, Checkbox, Tooltip, Autocomplete,
    Divider
} from '@mui/material';
import { ImagePaths } from '../../Utility/ImagePaths';
import RestAPIService from '../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../Utility/API/Serviceurls';
import { Label_Layout_Default_JSON } from '../../Utility/Constants';
import CommonValidation from '../../Components/CommonFunctions/CommonValidation';
import { Colors } from '../../Styles/Colors';
import Loader from '../../Components/Loader';
import { CheckAccessFunc } from '../../Components/CommonFunctions/CommonFunctions';

const formatList = [
    { label: "Portrait", value: "Portrait" },
    { label: "Landscape", value: "Landscape" },
]

const fontSizeList = [7, 8, 9, 10, 11, 12];

const maxLimits = {
    cardWidth: 10,
    cardHeight: 10,
    marginTop: 1.5,
    marginBottom: 1.5,
    marginLeft: 1.5,
    marginRight: 1.5,
    row: 24,
    column: 24,
};

const findValue = (list = [], value) => {
    return list?.find(item => item.value === value) || null;
}

const horizontalList = [
    { value: "left", title: "Align Left", img: ImagePaths.AlignLeft.default, disImg: ImagePaths.DisAlignLeft.default },
    { value: "center", title: "Align Center", img: ImagePaths.AlignCenter.default, disImg: ImagePaths.DisAlignCenter.default },
    { value: "right", title: "Align Right", img: ImagePaths.AlignRight.default, disImg: ImagePaths.DisAlignRight.default },
]
const verticalList = [
    { value: "top", title: "Align Top", img: ImagePaths.AlignTop.default, disImg: ImagePaths.DisAlignTop.default },
    { value: "middle", title: "Align Middle", img: ImagePaths.AlignMiddle.default, disImg: ImagePaths.DisAlignMiddle.default },
    { value: "bottom", title: "Align Bottom", img: ImagePaths.AlignBottom.default, disImg: ImagePaths.DisAlignBottom.default },
]

const IPLabelLayoutConfig = () => {
    const [state, setState] = useState({
        previewUrl: "",
        cardWidth: "6",
        cardHeight: "3",
        marginTop: "0.5",
        marginBottom: "0.5",
        marginLeft: "0.5",
        marginRight: "0.5",
        fontSize: 7,
        page_orientation: "Portrait",
        card_detail_data: JSON.parse(JSON.stringify(Label_Layout_Default_JSON)),
        row: 1,
        column: 1,
        horizontal_aligment: "left",
        vertical_aligment: "top",
        is_state_updated: false,
        isErrorMsg: false,
        isErrorMsgText: '',
        successMsg: false,
        successMsgText: '',
        isLoader: false,
    });

    useEffect(() => {
        getLabelLayoutDetails()
    }, [])

    useEffect(() => {
        if (state.is_state_updated) {
            getCustomCardPreview()
        }
    }, [state.is_state_updated])

    const updateState = (data) => {
        setState((prev) => ({
            ...prev,
            ...data
        }))
    }

    const LoaderFunction = (key) => {
        updateState({
            isLoader: key
        })
    }

    const errorMessage = (message) => {
        updateState({
            isErrorMsg: true,
            isErrorMsgText: message
        })
    }

    const successMessage = (message) => {
        updateState({
            successMsg: true,
            successMsgText: message
        })
    }

    const msgClose = () => {
        updateState({
            isErrorMsg: false,
            isErrorMsgText: '',
            successMsg: false,
            successMsgText: ''
        })
    }

    const getLabelLayoutDetails = () => {
        try {
            LoaderFunction(true)
            RestAPIService.getAllTwo(Serviceurls.IP_LABEL_LAYOUT_CONFIG)
                .then((response) => {
                    if (response.data.status === "success") {
                        const Data = response.data.data
                        updateState({
                            cardWidth: Data?.sticker_setup?.width || 5,
                            cardHeight: Data?.sticker_setup?.height || 3,
                            fontSize: Data?.sticker_setup?.font_size || 7,
                            page_orientation: Data?.sticker_setup?.orientation || "Portrait",
                            marginTop: Data?.margin_setup?.top || "0",
                            marginBottom: Data?.margin_setup?.bottom || "0",
                            marginLeft: Data?.margin_setup?.left || "0",
                            marginRight: Data?.margin_setup?.right || "0",
                            card_detail_data: Data?.patient_details || JSON.parse(JSON.stringify(Label_Layout_Default_JSON)),
                            row: Data?.label_count?.rows || 1,
                            column: Data?.label_count?.columns || 1,
                            horizontal_aligment: Data?.content_alignment?.horizontal || "left",
                            vertical_aligment: Data?.content_alignment?.vertical || "top",
                            is_state_updated: true,
                        })
                    }
                }).catch((error) => {
                    LoaderFunction(false)
                    errorMessage(error.response.data.message || error.message)
                })
        } catch (e) {
            LoaderFunction(false)
            errorMessage(e.message)
        }
    }

    const getCustomCardPreview = () => {
        try {
            RestAPIService.getAll(`${Serviceurls.IP_LABEL_PRINT}?preview=true`)
                .then((response) => {
                    const file = new Blob(
                        [response.data],
                        { type: 'application/pdf' });
                    const fileURL = URL.createObjectURL(file);
                    updateState({ previewUrl: fileURL, is_state_updated: false })
                    LoaderFunction(false);
                }).catch((e) => {
                    errorMessage(e.message)
                    LoaderFunction(false);
                })
        }
        catch (e) {
            errorMessage(e.message)
            LoaderFunction(false);
        }
    }

    const resetCardDetails = () => {
        try {
            updateState({
                cardWidth: "6",
                cardHeight: "3",
                marginTop: "0.5",
                marginBottom: "0.5",
                marginLeft: "0.5",
                marginRight: "0.5",
                fontSize: 7,
                page_orientation: "Portrait",
                card_detail_data: JSON.parse(JSON.stringify(Label_Layout_Default_JSON)),
                row: 1,
                column: 1,
                horizontal_aligment: "left",
                vertical_aligment: "top",
                is_state_updated: false,
            })
        } catch (e) {
            LoaderFunction(false)
            errorMessage(e.message)
        }
    }

    const checkValidation = () => {
        const cardDetails = state?.card_detail_data.every(obj => !obj.is_active);

        if (state?.cardWidth < 6) {
            errorMessage("Card width should be Greater than or Equal to 6")
            return false
        } else if (state?.cardHeight < 3) {
            errorMessage("Card height should be Greater than or Equal to 3")
            return false
        } else if (cardDetails) {
            errorMessage("Patient details should not be empty")
            return false
        } else {
            return true
        }
    }

    const postCardDetails = () => {
        try {
            let validation = checkValidation()
            if (validation) {
                LoaderFunction(true)
                const data = {
                    "sticker_setup": {
                        "orientation": state.page_orientation,
                        "height": +state.cardHeight || 3,
                        "width": +state.cardWidth || 5,
                        "font_size": +state.fontSize || 7,
                    },
                    "label_count": {
                        "rows": +state.row || 1,
                        "columns": +state.column || 1
                    },
                    "margin_setup": {
                        "top": +state.marginTop || 0,
                        "bottom": +state.marginBottom || 0,
                        "left": +state.marginLeft || 0,
                        "right": +state.marginRight || 0,
                    },
                    "patient_details": state?.card_detail_data,
                    "content_alignment": {
                        "vertical": state.vertical_aligment || "bottom",
                        "horizontal": state.horizontal_aligment || "left",
                    },
                }
                RestAPIService.create(data, Serviceurls.IP_LABEL_LAYOUT_CONFIG)
                    .then((response) => {
                        if (response.data.status === "success") {
                            LoaderFunction(false)
                            successMessage(response.data.message)
                            getLabelLayoutDetails()
                        }
                    }).catch((error) => {
                        LoaderFunction(false)
                        errorMessage(error.response.data.message || error.message)
                    })
            }
        } catch (e) {
            LoaderFunction(false)
            errorMessage(e.message)
        }
    }

    const renderCardLeft = () => {
        try {
            return (
                <Paper className='eMed_iplabel_layout' elevation={2}>
                    <Typography className='eMed_refdoc_title'>{"Inpatient Label Layout Configuration"}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "73vh", overflowY: "auto" }}>
                        {state.previewUrl ? <iframe
                            style={{ width: "100%", height: "100%" }}
                            src={state.previewUrl}
                            type="application/pdf" />
                            : <Typography fontSize={"1vw"}>No Records to be shown</Typography>}
                    </Box>
                </Paper>
            )
        } catch (e) { errorMessage(e.message) }
    }

    const onChangeHandler = (value, stateKey) => {
        try {
            const max = maxLimits[stateKey];
            let isValid = false;
            if (stateKey === "row" || stateKey === "column") {
                isValid = (CommonValidation.numberOnly(value) && (!max || value <= max) && +value > 0) || value === "";
            } else {
                isValid = (CommonValidation.DecimalNumber(value) && (!max || value <= max) && value !== "00") || value === "";
            }

            if (isValid || max === undefined) {
                updateState({ [stateKey]: value });
            }
        } catch (error) {
            errorMessage(error.message)
        }
    }

    const renderTextField = (label, stateKey, fromMargin = false) => {
        return (
            <Box mb={"0.625vw"} display={"flex"} flexDirection={fromMargin ? "row" : "column"} gap={fromMargin ? "0.625vw" : "0"} alignItems={fromMargin ? "center" : "flex-start"}>
                <Typography fontWeight={600} fontSize={"0.8vw"} mb={"0.3vw"} flex={fromMargin ? "0 0 20%" : "unset"}>{label}</Typography>
                <TextField
                    size='small'
                    sx={{ width: fromMargin ? "10vw" : "100%" }}
                    autoComplete='off'
                    value={state[stateKey]}
                    onChange={(e) => { onChangeHandler(e.target.value, stateKey) }}
                    InputProps={{
                        endAdornment: (stateKey === "row" || stateKey === "column") ? null : <Typography>cm</Typography>,
                        emed_tid: `Field_${stateKey}`
                    }}
                />
            </Box>
        )
    }

    const renderAutoComplete = (label, statekey, list = [], showKey = "label", valueKey = "value") => {
        return (
            <Box mb={"0.625vw"}>
                {label && <Typography fontWeight={600} fontSize={"0.8vw"} mb={"0.3vw"}>{label}</Typography>}
                <Autocomplete
                    size="small"
                    options={list || []}
                    getOptionLabel={(option) => (typeof option === "string" ? option : option?.[showKey] ? option[showKey] || "" : JSON.stringify(option))}
                    value={statekey === "fontSize" ? state[statekey] : findValue(list, state[statekey]) || null}
                    disableClearable
                    onChange={(event, newValue) => {
                        onChangeHandler(statekey === "fontSize" ? newValue : newValue[valueKey], statekey)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            sx={{ width: "100%" }}
                            size='small'
                            autoComplete="off"
                            inputProps={{
                                ...params.inputProps,
                            }}
                        />
                    )}
                />
            </Box>
        );
    };

    const handleDrag = (e) => { updateState({ drag_label: e.currentTarget.id }) }

    const handleDrop = (e) => {
        const dragBox = (state.card_detail_data).find((field) => field.label === state.drag_label);
        const dropBox = (state.card_detail_data).find((field) => field.label === e.currentTarget.id);

        const dragBoxID = dragBox.id;
        const dropBoxID = dropBox.id;

        const dragBoxField = dragBox.field_name;
        const dropBoxField = dropBox.field_name;

        const dragBoxCheck = dragBox.is_active;
        const dropBoxCheck = dropBox.is_active;

        const dragBoxDefault = dragBox.is_default;
        const dropBoxDefault = dropBox.is_default;

        const newBoxState = (state.card_detail_data).map((field) => {
            if ((!dragBoxDefault && !dropBoxDefault) || (dragBoxDefault && dropBoxDefault)) {
                if (field.label === state.drag_label) {
                    field.id = dropBoxID;
                    field.field_name = dragBoxField;
                    field.is_active = dragBoxCheck;
                    field.is_default = dragBoxDefault;
                }
                if (field.label === e.currentTarget.id) {
                    field.id = dragBoxID;
                    field.field_name = dropBoxField;
                    field.is_active = dropBoxCheck;
                    field.is_default = dropBoxDefault;
                }
            }
            return field;
        });

        const SortData = newBoxState.sort((a, b) => { return a.id - b.id })

        updateState({ card_detail_data: SortData })
    }

    const handleCheckClick = (e) => {
        let { card_detail_data } = state

        const ID = e?.target?.id

        if (ID) {
            const Index = card_detail_data.findIndex((data) => {
                return data.id == ID
            })

            if (card_detail_data[Index].is_default) {

            } else {
                card_detail_data[Index].is_active = e.target.checked
            }

            updateState({ card_detail_data })
        } else {
            card_detail_data.forEach((item) => {
                const Index = card_detail_data.findIndex((data) => {
                    return data.id == item.id
                })

                if (card_detail_data[Index].is_default) {

                } else {
                    card_detail_data[Index].is_active = true
                }

                updateState({ card_detail_data })
            })
        }
    }

    const renderCardField = (data) => {
        return (
            data?.label === "Barcode" ?
                <div
                    draggable={false}
                    id={data.label}
                    className="eMed_demolayout_right2_field">
                    <FormControlLabel control={<Checkbox id={data.id} emed_tid="Card_Details_CheckBox" onChange={handleCheckClick} checked={data.is_active} />} label={data.label} />
                </div> :
                <div
                    draggable={true}
                    onDragOver={(e) => e.preventDefault()}
                    onDragStart={handleDrag}
                    onDrop={handleDrop}
                    id={data.label}
                    className="eMed_demolayout_right2_field">
                    <img className="eMed_demolayout_right2_field_img" src={ImagePaths.DragIndicator.default} alt="Drag Here" />
                    <FormControlLabel control={<Checkbox id={data.id} disabled={data?.label === "IP Admission Id"} emed_tid="Card_Details_CheckBox" onChange={handleCheckClick} checked={data.is_active} />} label={data.label} />
                </div>
        )
    }

    const renderContentAlignment = (list, stateKey) => {
        return (
            <Stack direction="row" spacing={1} alignItems="flex-end" mt="0.5vw">
                {list.map(({ value, title, img, disImg }) => (
                    <Tooltip key={title} placement="top" title={title}>
                        <Button
                            sx={{
                                minWidth: "unset",
                                width: "3.8dvw !important",
                                height: "2.5dvw !important",
                                p: 0, display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                            emed_tid={`${value}_align_button`}
                            onClick={() => updateState({ [stateKey]: value })}
                            variant={state[stateKey] === value ? "contained" : "outlined"}
                        >
                            <img
                                style={{ width: "1.3vw", height: "2vw" }}
                                src={state[stateKey] === value ? img : disImg}
                                alt={title}
                            />
                        </Button>
                    </Tooltip>
                ))}
            </Stack>)
    }

    const renderCardRight = () => {
        try {
            return (
                <Paper className='eMed_iplabel_layout' elevation={2}>
                    <Typography className='eMed_refdoc_title'>{"Label Layout Configuration"}</Typography>
                    <Box sx={{ height: "67vh", overflowY: "auto" }}>
                        <Box className="eMed_ipLabel_right_divs">
                            <Typography sx={{ fontWeight: "600" }}>Label Layout Field</Typography>
                            <Typography sx={{ fontSize: "0.8vw" }}>(Switch the fields according to your practice)</Typography>
                        </Box>
                        <Box className="eMed_ipLabel_right_divs">
                            <Typography sx={{ fontWeight: "600", fontSize: "1vw" }}>Sticker Setup</Typography>
                            <Box className='eMed_ipLabel_grid_container'>
                                {renderAutoComplete("Page Orientation", "page_orientation", formatList)}
                                {renderTextField("Height", "cardHeight")}
                            </Box>
                            <Box className='eMed_ipLabel_grid_container'>
                                {renderTextField("Width", "cardWidth")}
                                {renderAutoComplete("Font Size", "fontSize", fontSizeList)}
                            </Box>
                        </Box>
                        <Box className="eMed_ipLabel_right_divs">
                            <Typography sx={{ fontWeight: "600", fontSize: "1vw" }}>Default Label Count</Typography>
                            <Box className='eMed_ipLabel_grid_container'>
                                {renderTextField("Row(s)", "row")}
                                {renderTextField("Column(s)", "column")}
                            </Box>
                        </Box>
                        <Box className="eMed_ipLabel_right_divs">
                            <Typography sx={{ fontWeight: "600", fontSize: "1vw" }}>Margin Setup</Typography>
                            <Typography fontSize={"0.8vw"} mb={"0.3vw"} color={"#8a8585"}>Settings</Typography>
                            {renderTextField("Top", "marginTop", true)}
                            {renderTextField("Bottom", "marginBottom", true)}
                            {renderTextField("Left", "marginLeft", true)}
                            {renderTextField("Right", "marginRight", true)}
                        </Box>
                        <Box className="eMed_ipLabel_right_divs">
                            <Typography sx={{ fontWeight: "600", fontSize: "1vw" }}>Patient Details</Typography>
                            {state.card_detail_data.map((item) => { return (renderCardField(item)) })}
                        </Box>
                        <Box className="eMed_ipLabel_right_divs">
                            <Typography sx={{ fontWeight: "600", fontSize: "1vw" }}>Content Alignment</Typography>
                            <Typography fontSize={"0.8vw"} mb={"0.3vw"} color={"#8a8585"}>( Switch the fields according to your practice )</Typography>
                            <Box display={"flex"} justifyContent={"center"} alignItems={"center"} gap={"0.625vw"}>
                                {renderContentAlignment(horizontalList, "horizontal_aligment")}
                                <Divider orientation="vertical" sx={{
                                    borderRightWidth: "3px",
                                    height: "2.5dvw",
                                    alignSelf: "center",
                                    mx: "0.5vw"
                                }} />
                                {renderContentAlignment(verticalList, "vertical_aligment")}
                            </Box>
                        </Box>
                    </Box>
                    <Stack className='eMed_DisSum_right_Btn' spacing={2} direction="row" sx={{ backgroundColor: Colors.DataTblHeaderbg }} alignItems="center" justifyContent="center" height={"6vh"}>
                        <Button emed_tid={'Reset_Button'} disabled={state?.isLoader} variant='outlined' size="small" id="eMed_Config_btn" onClick={resetCardDetails}>{"Reset"}</Button>
                        <Button emed_tid={'Post_Button'}
                            disabled={CheckAccessFunc("front_office", "Settings", "Card Layout Configuration", "Inpatient Label Layout Configuration", "SubTab")?.editAccess ? state?.isLoader : true}
                            variant='contained' size="small" id="eMed_Config_btn"
                            onClick={postCardDetails}>{"Save"}</Button>
                    </Stack>
                </Paper>
            )
        } catch (error) {
            errorMessage(error.message)
        }
    }

    return (
        <Box component={'div'} className='eMed_usrconf_container'>
            <Grid container spacing={1} sx={{ height: "100%" }}>
                <Grid item xs={8} sx={{ height: "100%" }}> {renderCardLeft()} </Grid>
                <Grid item xs={4} sx={{ height: "100%" }}> {renderCardRight()} </Grid>
            </Grid>
            {state.isErrorMsg && <ToastMsg severity={'error'} msg={state.isErrorMsgText} msgPop={msgClose.bind(this)} />}
            {state.successMsg && <ToastMsg severity={'success'} msg={state.successMsgText} msgPop={msgClose.bind(this)} />}
            <Loader loaderOpen={state.isLoader} />
        </Box>
    )
}

export default IPLabelLayoutConfig;